import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../config';
import {
  addCampaign,
  addCampaignSlot,
  deleteCampaign,
  deleteCampaignSlot,
  editCampaign,
  editCampaignSlot,
  getCampaign,
  getCampaignDictionaries,
  getCampaigns,
} from './methods';
import { EditCampaignRequest, EditCampaignSlotRequest } from './types';

const useGetCampaignsQuery = () => {
  const { isLoading, isSuccess, isError, data } = useQuery({
    queryFn: getCampaigns,
    queryKey: [queryKeys.campaignsKey],
    staleTime: Infinity,
  });

  return { data, isError, isLoading, isSuccess };
};

const useGetCampaignQuery = (id: number) => {
  const { isLoading, isSuccess, isError, data } = useQuery({
    enabled: !!id,
    queryFn: () => getCampaign(id),
    queryKey: [queryKeys.campaignsKey, id],
  });

  return { data, isError, isLoading, isSuccess };
};

const useGetCampaignDictionariesQuery = () => {
  const { isLoading, isSuccess, isError, data } = useQuery({
    queryFn: getCampaignDictionaries,
    queryKey: [queryKeys.campaignsKey, 'dictionaries'],
    staleTime: Infinity,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
  };
};

const useAddCampaignMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: addCampaignMutation,
  } = useMutation({
    mutationFn: addCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.campaignsKey] });
    },
  });

  return { addCampaignMutation, isError, isPending, isSuccess };
};

const useEditCampaignMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: editCampaignMutation,
  } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditCampaignRequest }) =>
      editCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.campaignsKey] });
    },
  });

  return { editCampaignMutation, isError, isPending, isSuccess };
};

const useDeleteCampaignMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: deleteCampaignMutation,
  } = useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.campaignsKey] });
    },
  });

  return { deleteCampaignMutation, isError, isPending, isSuccess };
};

const useAddCampaignSlotMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: addCampaignSlotMutation,
  } = useMutation({
    mutationFn: addCampaignSlot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.campaignsKey] });
    },
  });

  return { addCampaignSlotMutation, isError, isPending, isSuccess };
};

const useEditCampaignSlotMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: editCampaignSlotMutation,
  } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditCampaignSlotRequest }) =>
      editCampaignSlot(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.campaignsKey] });
    },
  });

  return { editCampaignSlotMutation, isError, isPending, isSuccess };
};

const useDeleteCampaignSlotMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: deleteCampaignSlotMutation,
  } = useMutation({
    mutationFn: deleteCampaignSlot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.campaignsKey] });
    },
  });

  return { deleteCampaignSlotMutation, isError, isPending, isSuccess };
};

export {
  useAddCampaignMutation,
  useAddCampaignSlotMutation,
  useDeleteCampaignMutation,
  useDeleteCampaignSlotMutation,
  useEditCampaignMutation,
  useEditCampaignSlotMutation,
  useGetCampaignDictionariesQuery,
  useGetCampaignQuery,
  useGetCampaignsQuery,
};
